import { doc, setDoc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db, FIREBASE_COLLECTIONS } from './firebase-config';
import { AdData, PaymentData, FormData } from './types';
import { telegramService } from './services/telegram-service';
import { AD_PRICE } from './utils/config';

export const generateAdId = (): string => {
    return 'ad_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

export const generateShortAdId = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

export const saveAdToFirebase = async (formData: FormData, tempAdId?: string): Promise<string | null> => {
    try {
        const adId = generateAdId();
        const shortId = generateShortAdId();
        
        // Log the temp ID mapping for debugging
        if (tempAdId) {
            console.log('🔄 Mapping temp ID to real ID:', tempAdId, '→', adId);
        }
        
        // Prepare ad data
        const adData: Omit<AdData, 'id' | 'createdAt' | 'updatedAt'> = {
            shortId,
            content: {
                html: formData.adContent,
                text: formData.adTextContent
            },
            button: {
                text: formData.buttonText,
                link: formData.buttonLink
            },
            image: formData.imagePreview ? {
                src: formData.imagePreview,
                alt: 'Ad image'
            } : null,
            metadata: {
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                characterCount: formData.adTextContent.length
            },
            status: 'pending_payment'
        };

        const adRecord = {
            id: adId,
            ...adData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        await setDoc(doc(db, FIREBASE_COLLECTIONS.ADS, adId), adRecord);
        console.log('Ad saved to Firebase with ID:', adId);
        
        // Send Telegram notification to admin - make it truly async and non-blocking
        // Use setTimeout to ensure this doesn't block the main flow
        setTimeout(async () => {
            try {
                const fullAdData: AdData = {
                    id: adId,
                    ...adData,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
                
                await telegramService.notifyAdCreated(fullAdData);
                console.log('✅ Telegram notification sent for new ad:', adId);
            } catch (telegramError) {
                console.error('❌ Failed to send Telegram notification for new ad:', telegramError);
                // Don't fail the whole operation if Telegram notification fails
            }
        }, 0);
        
        return adId;
    } catch (error) {
        console.error('Error saving ad to Firebase:', error);
        return null;
    }
};

export const updateAdData = async (adId: string, updateData: Partial<AdData>): Promise<boolean> => {
    try {
        if (!adId) {
            console.error('Invalid ad ID');
            return false;
        }

        const dataToUpdate = {
            ...updateData,
            updatedAt: serverTimestamp()
        };

        await updateDoc(doc(db, FIREBASE_COLLECTIONS.ADS, adId), dataToUpdate);
        console.log('Ad data updated:', adId, updateData);
        
        return true;
    } catch (error) {
        console.error('Error updating ad data:', error);
        return false;
    }
};

// Background version of updateAdData - doesn't block calling code
export const backgroundUpdateAdData = (adId: string, updateData: Partial<AdData>): void => {
    setTimeout(async () => {
        try {
            console.log('🔄 Background ad data update started for:', adId);
            const success = await updateAdData(adId, updateData);
            if (success) {
                console.log('✅ Background ad data update completed for:', adId);
            } else {
                console.log('❌ Background ad data update failed for:', adId);
            }
        } catch (error) {
            console.error('❌ Background ad data update error:', error);
        }
    }, 0);
};

export const updateAdPaymentStatus = async (adId: string, paymentData: PaymentData): Promise<boolean> => {
    try {
        if (!adId) {
            console.error('Invalid ad ID');
            return false;
        }

        // Get current ad data to check previous status, telegram username, and shortId
        let previousStatus: string | undefined;
        let telegramUsername: string | undefined;
        let shortAdId: string | undefined;
        try {
            const currentAdDoc = await getDoc(doc(db, FIREBASE_COLLECTIONS.ADS, adId));
            if (currentAdDoc.exists()) {
                const adData = currentAdDoc.data();
                previousStatus = adData.status;
                telegramUsername = adData.telegramUsername;
                shortAdId = adData.shortId;
            }
        } catch (error) {
            console.error('Error getting previous ad status:', error);
        }

        const updateData = {
            status: paymentData.status,
            paymentMethod: paymentData.method,
            paymentDetails: paymentData.details,
            updatedAt: serverTimestamp()
        };

        await updateDoc(doc(db, FIREBASE_COLLECTIONS.ADS, adId), updateData);
        console.log('Ad payment status updated:', adId, paymentData.status);
        
        // Send Telegram notification to admin - now fully async
        try {
            await telegramService.notifyPaymentStatusChange(adId, paymentData, previousStatus, telegramUsername, shortAdId);
            console.log('Telegram notification sent for payment status change:', adId, paymentData.status);
        } catch (telegramError) {
            console.error('Failed to send Telegram notification for payment status change:', telegramError);
            // Don't fail the whole operation if Telegram notification fails
        }
        
        return true;
    } catch (error) {
        console.error('Error updating ad payment status:', error);
        return false;
    }
};

// Background version of updateAdPaymentStatus - doesn't block calling code
export const backgroundUpdateAdPaymentStatus = (adId: string, paymentData: PaymentData): void => {
    setTimeout(async () => {
        try {
            console.log('🔄 Background payment status update started for:', adId);
            const success = await updateAdPaymentStatus(adId, paymentData);
            if (success) {
                console.log('✅ Background payment status update completed for:', adId);
            } else {
                console.log('❌ Background payment status update failed for:', adId);
            }
        } catch (error) {
            console.error('❌ Background payment status update error:', error);
        }
    }, 0);
};

export const getAdFromFirebase = async (adId: string): Promise<AdData | null> => {
    try {
        if (!adId) {
            console.error('Invalid ad ID');
            return null;
        }

        const docRef = doc(db, FIREBASE_COLLECTIONS.ADS, adId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data() as AdData;
        } else {
            console.log('No ad found with ID:', adId);
            return null;
        }
    } catch (error) {
        console.error('Error getting ad from Firebase:', error);
        return null;
    }
};

export const markPaymentCompleted = async (adId: string): Promise<boolean> => {
    try {
        if (!adId) {
            console.error('Invalid ad ID');
            return false;
        }

        // Get current ad data to get payment method, previous status, telegram username, and shortId
        let previousStatus: string | undefined;
        let paymentMethod: 'crypto' | 'card' | undefined;
        let telegramUsername: string | undefined;
        let shortAdId: string | undefined;
        let currentAdData: any = null;
        try {
            const currentAdDoc = await getDoc(doc(db, FIREBASE_COLLECTIONS.ADS, adId));
            if (currentAdDoc.exists()) {
                currentAdData = currentAdDoc.data();
                previousStatus = currentAdData.status;
                paymentMethod = currentAdData.paymentMethod;
                telegramUsername = currentAdData.telegramUsername;
                shortAdId = currentAdData.shortId;
            }
        } catch (error) {
            console.error('Error getting current ad data:', error);
        }

        const updateData = {
            status: 'payment_completed' as const,
            paymentCompletedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        await updateDoc(doc(db, FIREBASE_COLLECTIONS.ADS, adId), updateData);
        console.log('Payment status updated to completed for ad:', adId);
        
        // Send Telegram notification to admin
        try {
            let paymentType: 'cryptocurrency' | 'stripe_checkout' = 'stripe_checkout';
            if (paymentMethod === 'crypto') {
                paymentType = 'cryptocurrency';
            }
            
            const paymentData: PaymentData = {
                status: 'payment_completed',
                method: paymentMethod,
                details: {
                    type: paymentType,
                    amount: AD_PRICE,
                    currency: 'USD',
                    initiatedAt: new Date().toISOString(),
                    sessionId: currentAdData?.paymentDetails?.sessionId
                }
            };
            
            await telegramService.notifyPaymentStatusChange(adId, paymentData, previousStatus, telegramUsername, shortAdId);
            console.log('Telegram notification sent for payment completion:', adId);
        } catch (telegramError) {
            console.error('Failed to send Telegram notification for payment completion:', telegramError);
            // Don't fail the whole operation if Telegram notification fails
        }
        
        return true;
    } catch (error) {
        console.error('Error updating payment status:', error);
        return false;
    }
}; 